import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IItem } from '@rbi-ctg/menu';
import { AddExtrasDebug } from 'components/debug-menu/add-extras-debug';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { AddOnSelections, IAddOnSection, IAddOnSectionOption } from 'hooks/use-cart-add-ons';
import { useFormatCalories } from 'hooks/use-format-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { MAX_ADD_ON_QUANTITY } from 'utils/cart';
import { getItemCalories } from 'utils/menu';
import { escapeQuantity } from 'utils/wizard';

import { AddExtrasAccordion } from './add-extras-accordion';
import { AddExtrasContentItem } from './add-extras-content-item';
import { AddExtrasDebugMenuContainer, ItemRow } from './styled';

interface IAddExtrasProps extends IBaseProps {
  addOnSelections: AddOnSelections;
  addOns: IAddOnSection[];
  updateQuantityForitem: (
    item: IAddOnSectionOption,
    quantity: number,
    section: IAddOnSection
  ) => void;
}

export const AddExtrasContent: React.FC<React.PropsWithChildren<IAddExtrasProps>> = ({
  addOns,
  addOnSelections,
  updateQuantityForitem,
}) => {
  const isDebugMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);

  const { formatMessage } = useIntl();
  const { pricingFunction } = useMenuContext();
  const formatCalories = useFormatCalories();

  const getCalorieString = (item: IItem) => formatCalories(getItemCalories(item));

  const getItemMax = (
    itemOptionId: string,
    itemMax: number | null,
    sectionId: string,
    sectionMax: number | null
  ) => {
    const maxForThisItem = escapeQuantity(itemMax ?? MAX_ADD_ON_QUANTITY);
    if (!sectionMax || sectionMax === 0) {
      return maxForThisItem;
    }

    // How many are we at for the section currently?
    const totalForSection = Object.values(addOnSelections).reduce(
      (total, { sectionId: selectionSectionId, quantity: selectionQuantity }) => {
        return selectionSectionId === sectionId ? total + selectionQuantity : total;
      },
      0
    );

    // Did we hit the sectionMax?
    // If so return the current selection or zero
    if (totalForSection === sectionMax) {
      return addOnSelections[itemOptionId]?.quantity ?? 0;
    }

    // If not continue as normal
    return maxForThisItem;
  };

  return (
    <>
      {addOns.map(addOnSection => (
        <AddExtrasAccordion
          key={addOnSection._id}
          title={addOnSection.name.locale}
          subtitle={
            addOnSection.maxAmount > 0
              ? formatMessage({ id: 'totalChoices' }, { amount: addOnSection.maxAmount })
              : ''
          }
        >
          <Box
            testID={`add-on-section-${addOnSection.name.locale}`}
            accessibilityLabel={addOnSection.name.locale}
          >
            <VisuallyHidden accessibilityLabel={addOnSection.name.locale} />
            {addOnSection?.options?.map((addOnOption, i) => {
              const addOnOptionChildren = (
                <ItemRow key={addOnOption._key} isLastItem={i === addOnSection.options.length - 1}>
                  <AddExtrasContentItem
                    itemName={addOnOption.option.name.locale}
                    itemCalories={getCalorieString(addOnOption.option)}
                    index={addOnSelections[addOnOption._key]?.quantity ?? 0}
                    onChange={newQuantity => {
                      updateQuantityForitem(addOnOption, newQuantity, addOnSection);
                    }}
                    itemPrice={pricingFunction({ item: addOnOption.option })}
                    itemImage={addOnOption.option.image}
                    isInCart={
                      addOnSelections[addOnOption._key]?.data.name.locale ===
                      addOnOption.option.name.locale
                    }
                    max={getItemMax(
                      addOnOption._key,
                      addOnOption.maxAmount,
                      addOnSection._id,
                      addOnSection.maxAmount
                    )}
                    maxAmount={addOnSection.maxAmount}
                  />
                </ItemRow>
              );

              return isDebugMenuEnabled ? (
                <AddExtrasDebugMenuContainer key={`add-extras-debug-menu-${addOnOption._key}`}>
                  <AddExtrasDebug addOn={addOnOption.option} />
                  {addOnOptionChildren}
                </AddExtrasDebugMenuContainer>
              ) : (
                addOnOptionChildren
              );
            })}
          </Box>
        </AddExtrasAccordion>
      ))}
    </>
  );
};
